"use client";

import { isBackendConfigured } from "@/api/api-client";
import { fetchAztangofestBookingSummary } from "@/api/aztangofest/fetchAztangofestBookingSummary";
import { postAztangofestBookingParticipantsUpsert } from "@/api/aztangofest/postAztangofestBookingParticipantsUpsert";
import { buildDraftBookingParticipantsUpsertBody } from "@/api/aztangofest/buildDraftBookingParticipantsUpsertBody";
import { pullBookingParticipantsIntoStore } from "@/lib/bookingParticipantsRemoteSync";
import { executeRecaptchaAction } from "@/controllers/RecaptchaController";
import {
    decodeJwtSub,
    getAzDanceBookingToken,
    isAzDanceBookingTokenValid,
} from "@/lib/azdanceBookingSession";
import { ensureFestDraftEventBookingIfMissing } from "@/lib/ensureFestDraftEventBookingIfMissing";
import {
    useRegistrationStore,
    type RegisteredParticipant,
} from "@/state/stores/registrationStore";
import { useActivityStore } from "@/state/stores/activityStore";

const SERVER_PARTICIPANT_ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/i;

export type RegistrationFlowSyncBookingParticipantsOpts = {
    /**
     * Тело draft-upsert: если не передано — текущий `registeredParticipants` из стора
     * (после `registerParticipants` должен совпадать с воронкой).
     */
    draftUpsertParticipants?: RegisteredParticipant[];
    /**
     * После успешного POST (confirmed и draft).
     * По умолчанию — только pull в стор.
     */
    reloadAfterSync?: (language: string) => Promise<void>;
    /**
     * После `reloadAfterSync` (remap id в корзине после pull; и для confirmed).
     */
    afterDraftReload?: (ctx: {
        language: string;
        summaryParticipantIds: string[];
        listBeforePull: RegisteredParticipant[];
        /** Ограничить remap только этими id (добавление участника). */
        remapParticipantIds?: Set<string>;
    }) => Promise<void>;
    /** Передаётся в `afterDraftReload` как `remapParticipantIds`. */
    remapCartAfterPullOnlyParticipantIds?: Set<string>;
};

/**
 * Как {@link useRegistrationFlowPage} `handleSubmit`: summary → POST participants
 * (draft-shaped upsert и для confirmed — см. бэкенд: create без id + patch с id), затем перезагрузка списка.
 * `dashboardEventBookingId` при необходимости добивается через {@link ensureFestDraftEventBookingIfMissing}.
 */
export async function registrationFlowSyncBookingParticipantsToApi(
    language: string,
    opts?: RegistrationFlowSyncBookingParticipantsOpts,
): Promise<boolean> {
    let eid = useRegistrationStore.getState().dashboardEventBookingId?.trim();
    const token = getAzDanceBookingToken()?.trim();
    if (!isBackendConfigured() || !token || !isAzDanceBookingTokenValid()) {
        return false;
    }
    if (!eid) {
        await ensureFestDraftEventBookingIfMissing(language);
        eid = useRegistrationStore.getState().dashboardEventBookingId?.trim();
    }
    if (!eid) return false;

    const summary = await fetchAztangofestBookingSummary(
        { eventBookingId: eid },
        language,
        token,
    );
    const storeList = useRegistrationStore.getState().registeredParticipants;
    const draftList = opts?.draftUpsertParticipants ?? storeList;

    const reload =
        opts?.reloadAfterSync ??
        (async (lang: string) => {
            await pullBookingParticipantsIntoStore(lang);
        });

    const listBeforePull = [
        ...useRegistrationStore.getState().registeredParticipants,
    ];
    const jwtSub = decodeJwtSub(token);
    const cart = useActivityStore.getState().cart;
    const recaptchaUpsert = await executeRecaptchaAction(
        "UpsertBookingParticipants",
    );
    const upsertBody = buildDraftBookingParticipantsUpsertBody(
        draftList,
        summary.participants,
        jwtSub,
        cart,
    );
    await postAztangofestBookingParticipantsUpsert({
        body: upsertBody,
        accessToken: token,
        language,
        recaptchaToken: recaptchaUpsert,
    });
    await reload(language);
    if (opts?.afterDraftReload) {
        await opts.afterDraftReload({
            language,
            summaryParticipantIds: summary.participants.map((p) => p.id),
            listBeforePull,
            remapParticipantIds: opts.remapCartAfterPullOnlyParticipantIds,
        });
    }
    return true;
}

function cartParticipantRemapKey(
    p: RegisteredParticipant,
): string | null {
    const fn = p.firstName?.trim().toLowerCase() ?? "";
    const ln = p.lastName?.trim().toLowerCase() ?? "";
    if (!fn && !ln) return null;
    const age =
        p.age !== undefined && p.age !== null ? String(p.age) : "";
    const g = (p.gender ?? "").toString().toLowerCase();
    const r = (p.role ?? "").toString().toLowerCase();
    return `${fn}|${ln}|${age}|${g}|${r}`;
}

function bucketParticipantsByRemapKey(
    list: RegisteredParticipant[],
): Map<string, RegisteredParticipant[]> {
    const out = new Map<string, RegisteredParticipant[]>();
    for (const p of list) {
        const k = cartParticipantRemapKey(p);
        if (!k) continue;
        const prev = out.get(k);
        if (prev) prev.push(p);
        else out.set(k, [p]);
    }
    return out;
}

/**
 * Remap ephemeral cart participant ids to server ULIDs after pull.
 * Email is not used (not unique). Remap only when exactly one new ULID row and
 * exactly one ephemeral row share the same composite display key.
 */
export function remapCartParticipantIdsAfterParticipantPull(opts: {
    listBeforePull: RegisteredParticipant[];
    summaryParticipantIds: string[];
    onlyParticipantIds?: Set<string>;
}): void {
    const serverBefore = new Set(
        opts.summaryParticipantIds.map((id) => id.trim()).filter(Boolean),
    );
    const mergedAfter =
        useRegistrationStore.getState().registeredParticipants;
    const rows =
        opts.onlyParticipantIds && opts.onlyParticipantIds.size > 0
            ? opts.listBeforePull.filter((r) =>
                  opts.onlyParticipantIds!.has(r.id),
              )
            : opts.listBeforePull;

    const ephemerals = rows.filter(
        (row) =>
            !serverBefore.has(row.id) &&
            !SERVER_PARTICIPANT_ULID_RE.test(row.id),
    );
    const newServerRows = mergedAfter.filter(
        (m) =>
            SERVER_PARTICIPANT_ULID_RE.test(m.id) &&
            !serverBefore.has(m.id.trim()),
    );

    const ephemeralByKey = bucketParticipantsByRemapKey(ephemerals);
    const newByKey = bucketParticipantsByRemapKey(newServerRows);

    for (const [key, ephList] of ephemeralByKey) {
        const newList = newByKey.get(key);
        if (!newList || ephList.length !== 1 || newList.length !== 1) continue;
        const row = ephList[0];
        const match = newList[0];
        if (match.id !== row.id) {
            useActivityStore
                .getState()
                .remapParticipantIdsInCart(row.id, match.id);
        }
    }
}
